<div>
  @if (isPPWRAllowed) {
    <!-- PPWR Informationen -->
    <section class="content-section">
      <div class="section-header ppwr-header">
        <div>
          <span class="section-kicker">{{ 'dataManagement.organizationSettings.sections.ppwrInformation' | transloco }}</span>
          <h2>{{ 'dataManagement.organizationSettings.sections.rolesAndParticipants' | transloco }}</h2>
          <p>{{ 'dataManagement.organizationSettings.descriptions.ppwrRoles' | transloco }}</p>
        </div>
        <div class="info-callout">
          <mat-icon>tips_and_updates</mat-icon>
          <span>{{ 'dataManagement.organizationSettings.descriptions.tip' | transloco }}</span>
        </div>
      </div>

      <div class="role-grid">
        <!-- Manufacturer -->
        <article class="role-card">
          <div class="role-card-header">
            <div>
              <mat-icon>factory</mat-icon>
              <h3 [matBadge]="manufacturers().length" [matBadgeColor]="manufacturers().length >= maxEntriesPerRole ? 'warn' : 'primary'" matBadgeOverlap="false">
                {{ 'createDoc.createDocDialog.producerLabel' | transloco }}
              </h3>
            </div>
            <mat-form-field appearance="outline" class="role-search" subscriptSizing="dynamic">
              <mat-icon matPrefix>search</mat-icon>
              <input
                matInput
                [formControl]="manufacturerSearchControl"
                [placeholder]="'common.buttons.search' | transloco"
                [attr.aria-label]="'common.buttons.search' | transloco" />
            </mat-form-field>
            <button
              mat-stroked-button
              color="primary"
              type="button"
              [disabled]="manufacturers().length >= maxEntriesPerRole"
              (click)="openManufacturerDialog()">
              <mat-icon>add</mat-icon>{{ 'common.buttons.add' | transloco }}
            </button>
          </div>
          <div class="role-list">
            @if (manufacturers().length === 0) {
              <p class="empty-text">
                {{ 'dataManagement.organizationSettings.emptyStates.manufacturers' | transloco }}
              </p>
            } @else if (filteredManufacturers().length === 0) {
              <p class="empty-text">
                {{ 'common.text.noResults' | transloco }}
              </p>
            }
            @for (item of filteredManufacturers(); track $index) {
              <div class="role-item" [class.readonly]="item === selfManufacturer()">
                <div>
                  <strong>{{ item.companyName }}</strong>
                  <span>{{ item.address }} {{ item.postCode }} {{ item.city }}</span>
                </div>
                @if (item === selfManufacturer()) {
                  <mat-icon class="role-state">verified</mat-icon>
                } @else {
                  <div class="item-actions">
                    <button mat-icon-button color="primary" type="button" [attr.aria-label]="'common.buttons.edit' | transloco" [matTooltip]="'common.buttons.edit' | transloco" (click)="editManufacturer(manufacturers().indexOf(item))">
                      <mat-icon>edit</mat-icon>
                    </button>
                    <button
                      mat-icon-button
                      color="warn"
                      [attr.aria-label]="'dataManagement.organizationSettings.aria.deleteManufacturer' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.deleteManufacturer' | transloco"
                      (click)="deleteManufacturer(manufacturers().indexOf(item))">
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        </article>

        <!-- Authorized Representative -->
        <article class="role-card">
          <div class="role-card-header">
            <div>
              <mat-icon>assignment_ind</mat-icon>
              <h3
                [matBadge]="authorizedRepresentatives().length"
                [matBadgeColor]="authorizedRepresentatives().length >= maxEntriesPerRole ? 'warn' : 'primary'"
                matBadgeOverlap="false">
                {{ 'createDoc.createDocDialog.representativeTitle' | transloco }}
              </h3>
            </div>
            <mat-form-field appearance="outline" class="role-search" subscriptSizing="dynamic">
              <mat-icon matPrefix>search</mat-icon>
              <input
                matInput
                [formControl]="representativeSearchControl"
                [placeholder]="'common.buttons.search' | transloco"
                [attr.aria-label]="'common.buttons.search' | transloco" />
            </mat-form-field>
            <button
              mat-stroked-button
              color="primary"
              type="button"
              [disabled]="authorizedRepresentatives().length >= maxEntriesPerRole"
              (click)="openAuthorizedRepresentativeDialog()">
              <mat-icon>add</mat-icon>{{ 'common.buttons.add' | transloco }}
            </button>
          </div>
          <p class="role-description">
            {{ 'dataManagement.organizationSettings.descriptions.authorizedRepresentative' | transloco }}
          </p>
          <div class="role-list">
            @if (authorizedRepresentatives().length === 0) {
              <p class="empty-text">
                {{ 'dataManagement.organizationSettings.emptyStates.authorizedRepresentatives' | transloco }}
              </p>
            } @else if (filteredAuthorizedRepresentatives().length === 0) {
              <p class="empty-text">
                {{ 'common.text.noResults' | transloco }}
              </p>
            }
            @for (item of filteredAuthorizedRepresentatives(); track $index) {
              <div class="role-item">
                <div>
                  <strong>{{ item.companyName }}</strong>
                  <span>{{ item.address }} {{ item.postCode }} {{ item.city }}</span>
                </div>
                <div class="item-actions">
                  <button
                    mat-icon-button
                    color="primary"
                    type="button"
                    [attr.aria-label]="'common.buttons.edit' | transloco" [matTooltip]="'common.buttons.edit' | transloco"
                    (click)="editAuthorizedRepresentative(authorizedRepresentatives().indexOf(item))">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button
                    mat-icon-button
                    color="warn"
                    [attr.aria-label]="'dataManagement.organizationSettings.aria.deleteAuthorizedRepresentative' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.deleteAuthorizedRepresentative' | transloco"
                    (click)="deleteAuthorizedRepresentative(authorizedRepresentatives().indexOf(item))">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            }
          </div>
        </article>

        <!-- CEO -->
        <article class="role-card">
          <div class="role-card-header">
            <div>
              <mat-icon>supervisor_account</mat-icon>
              <h3 [matBadge]="ceos().length" [matBadgeColor]="ceos().length >= maxEntriesPerRole ? 'warn' : 'primary'" matBadgeOverlap="false">
                {{ 'createDoc.signersDialog.managingDirectorLabel' | transloco }}
              </h3>
            </div>
            <mat-form-field appearance="outline" class="role-search" subscriptSizing="dynamic">
              <mat-icon matPrefix>search</mat-icon>
              <input
                matInput
                [formControl]="ceoSearchControl"
                [placeholder]="'common.buttons.search' | transloco"
                [attr.aria-label]="'common.buttons.search' | transloco" />
            </mat-form-field>
            <button mat-stroked-button color="primary" type="button" [disabled]="ceos().length >= maxEntriesPerRole" (click)="openCeoDialog()">
              <mat-icon>add</mat-icon>{{ 'common.buttons.add' | transloco }}
            </button>
          </div>
          <div class="role-list">
            @if (ceos().length === 0) {
              <p class="empty-text">
                {{ 'dataManagement.organizationSettings.emptyStates.ceos' | transloco }}
              </p>
            } @else if (filteredCeos().length === 0) {
              <p class="empty-text">
                {{ 'common.text.noResults' | transloco }}
              </p>
            }
            @for (item of filteredCeos(); track $index) {
              <div class="role-item">
                <div>
                  <strong>{{ item.firstName }} {{ item.lastName }}</strong>
                  <span>{{ item.email }}</span>
                </div>
                <div class="item-actions">
                  <span class="status-tag" [class.active]="item.signatureImageId" [class.neutral]="!item.signatureImageId">
                    <span class="status-dot" [class.neutral]="!item.signatureImageId"></span>
                    {{ (item.signatureImageId ? 'dataManagement.organizationSettings.fields.signaturePresent' : 'dataManagement.organizationSettings.fields.noSignature') | transloco }}
                  </span>
                  @if (item.personProfileId > 0) {
                    <button
                      mat-icon-button
                      color="primary"
                      [attr.aria-label]="'dataManagement.organizationSettings.aria.editSignature' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.editSignature' | transloco"
                      (click)="openSignatureDialog(item, ceos)">
                      <mat-icon>draw</mat-icon>
                    </button>
                  }
                  <button
                    mat-icon-button
                    color="primary"
                    [attr.aria-label]="'dataManagement.organizationSettings.aria.editCeo' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.editCeo' | transloco"
                    (click)="editCeo(ceos().indexOf(item))">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button
                    mat-icon-button
                    color="warn"
                    [attr.aria-label]="'dataManagement.organizationSettings.aria.deleteCeo' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.deleteCeo' | transloco"
                    (click)="deleteCeo(ceos().indexOf(item))">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            }
          </div>
        </article>

        <!-- Signee -->
        <article class="role-card">
          <div class="role-card-header">
            <div>
              <mat-icon>draw</mat-icon>
              <h3 [matBadge]="signees().length" [matBadgeColor]="signees().length >= maxEntriesPerRole ? 'warn' : 'primary'" matBadgeOverlap="false">
                {{ 'createDoc.signersDialog.otherSignerTitle' | transloco }}
              </h3>
            </div>
            <mat-form-field appearance="outline" class="role-search" subscriptSizing="dynamic">
              <mat-icon matPrefix>search</mat-icon>
              <input
                matInput
                [formControl]="signeeSearchControl"
                [placeholder]="'common.buttons.search' | transloco"
                [attr.aria-label]="'common.buttons.search' | transloco" />
            </mat-form-field>
            <button mat-stroked-button color="primary" type="button" [disabled]="signees().length >= maxEntriesPerRole" (click)="openSigneeDialog()">
              <mat-icon>add</mat-icon>{{ 'common.buttons.add' | transloco }}
            </button>
          </div>
          <div class="role-list">
            @if (signees().length === 0) {
              <p class="empty-text">
                {{ 'dataManagement.organizationSettings.emptyStates.signees' | transloco }}
              </p>
            } @else if (filteredSignees().length === 0) {
              <p class="empty-text">
                {{ 'common.text.noResults' | transloco }}
              </p>
            }
            @for (item of filteredSignees(); track $index) {
              <div class="role-item">
                <div>
                  <strong>{{ item.firstName }} {{ item.lastName }}</strong>
                  <span>{{ item.function || item.email }}</span>
                </div>
                <div class="item-actions">
                  <span class="status-tag" [class.active]="item.signatureImageId" [class.neutral]="!item.signatureImageId">
                    <span class="status-dot" [class.neutral]="!item.signatureImageId"></span>
                    {{ (item.signatureImageId ? 'dataManagement.organizationSettings.fields.signaturePresent' : 'dataManagement.organizationSettings.fields.noSignature') | transloco }}
                  </span>
                  @if (item.personProfileId > 0) {
                    <button
                      mat-icon-button
                      color="primary"
                      [attr.aria-label]="'dataManagement.organizationSettings.aria.editSignature' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.editSignature' | transloco"
                      (click)="openSignatureDialog(item, signees)">
                      <mat-icon>draw</mat-icon>
                    </button>
                  }
                  <button
                    mat-icon-button
                    color="primary"
                    [attr.aria-label]="'dataManagement.organizationSettings.aria.editSignee' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.editSignee' | transloco"
                    (click)="editSignee(signees().indexOf(item))">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button
                    mat-icon-button
                    color="warn"
                    [attr.aria-label]="'dataManagement.organizationSettings.aria.deleteSignee' | transloco" [matTooltip]="'dataManagement.organizationSettings.aria.deleteSignee' | transloco"
                    (click)="deleteSignee(signees().indexOf(item))">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            }
          </div>
        </article>
      </div>
    </section>

    <div class="sticky-actions">
      <button mat-button type="button" [disabled]="!hasChanges() || isSaving()" (click)="cancelChanges()">
        {{ 'common.buttons.cancel' | transloco }}
      </button>
      <button mat-raised-button color="primary" type="button" [disabled]="!hasChanges() || isSaving()" (click)="saveChanges()">
        <mat-icon>save</mat-icon>
        {{ 'common.buttons.save' | transloco }}
      </button>
    </div>
  }
</div>
